// Planets: the prestige layer above the death/skill-point loop. Each planet has
// a conquest goal (clear N waves). Conquering it lets the player ascend: a big
// reset that grants cosmic currency (permanent global multipliers) and moves to
// the next, harder planet. Skill tree and cosmic carry across planets.

import { BALANCE } from './balance';

// Turkish names for the sky: planets and stars from Turkic astronomy.
const PLANET_NAMES = [
  'Dünya', // Earth
  'Ay', // Moon
  'Merih', // Mars
  'Ülker', // Pleiades
  'Demirkazık', // Polaris
  'Kervankıran', // Venus (morning star)
  'Çoban Yıldızı', // Venus (evening star)
  'Yedi Kardeş', // the Big Dipper
  'Terazi', // Libra
  'Akyıldız', // bright star
  'Gökçe', // sky-blue
  'Samanyolu', // the Milky Way
];

const ROMAN = ['', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

/** A flavour name for a planet index, cycling with a numeral suffix past the list. */
export function planetName(index: number): string {
  const base = PLANET_NAMES[index % PLANET_NAMES.length];
  const cycle = Math.floor(index / PLANET_NAMES.length);
  return cycle === 0 ? base : `${base} ${ROMAN[cycle] ?? cycle + 1}`;
}

/** The age (era gate) that must be reached to conquer a given planet. Reaching
 *  it means clearing that many era gate bosses in one line. Clamped to the age
 *  cap: without it planet index 4+ would demand age 14 while checkGate stops at
 *  maxAge 12, silently dead-ending the prestige loop. Past the clamp, planet
 *  difficulty keeps scaling via planetThreatScale (1.8x per planet). */
export function conquestAge(planet: number): number {
  return Math.min(
    BALANCE.planets.conquestBaseAge + planet * BALANCE.planets.conquestAgePerPlanet,
    BALANCE.ages.maxAge,
  );
}

/** Threat multiplier applied to every wave on a given planet. */
export function planetThreatScale(planet: number): number {
  return Math.pow(BALANCE.planets.threatGrowth, planet);
}

/** Cosmic currency granted for conquering, scaling with planet, the age reached,
 *  and the overshoot beyond the conquest goal (so pushing deeper before ascending
 *  pays). */
export function cosmicGain(planet: number, age: number): number {
  const goal = conquestAge(planet);
  const overshoot = Math.max(1, age / goal);
  return Math.ceil((planet + 1) * (1 + age * BALANCE.planets.cosmicAgeFactor) * overshoot);
}
